import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, Sparkles, Gift, Check } from "lucide-react";

const Hero = () => {
  const { t } = useLanguage();
  const c = t.hero.conversion;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#f4f1ea] pt-32 sm:pt-40 pb-20 sm:pb-32 grain"
    >
      {/* Layer 1 — Parallax Paris skyline (bg-fixed, CSS-only, hydration-safe) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-center bg-cover opacity-[0.14] hero-parallax-paris"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=2000&q=70)",
          backgroundAttachment: "fixed",
        }}
      />
      {/* Layer 2 — Full background: Professor Lionel (aligned LEFT so the conversion card on the right stays clean) */}
      <div className="pointer-events-none absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_french-for-all-1/artifacts/fqb0z62o_Ame%CC%81lioration%20de%20la%20qualite%CC%81%20photo.png"
          alt="Professor Lionel, francês nativo de Paris, professor de francês online para brasileiros"
          loading="eager"
          decoding="async"
          className="absolute left-0 top-0 h-full w-auto max-w-none object-contain object-left brightness-[0.92] saturate-[0.8] blur-[3px] opacity-70"
        />
        <div className="absolute inset-0 bg-[#f4f1ea]/25" />
        {/* Fade cover the RIGHT side so the conversion card sits on a clean background */}
        <div className="absolute inset-y-0 right-0 w-3/5 bg-gradient-to-l from-[#f4f1ea] via-[#f4f1ea]/85 to-transparent" />
        {/* Soft veil on the left column where the H1 sits, so the text stays legible over the photo */}
        <div className="absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-[#f4f1ea]/60 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Left column — H1 + subtitle */}
        <div className="lg:col-span-7">
          <p className="overline reveal" data-testid="hero-overline">
            <Sparkles className="inline h-3 w-3 mr-2 -mt-0.5" strokeWidth={2} />
            {t.hero.overline}
          </p>

          <h1
            data-testid="hero-title"
            className="reveal reveal-delay-1 font-display mt-6 text-[44px] leading-[1.02] tracking-tight sm:text-6xl lg:text-[78px] text-[#1e2a5a]"
          >
            <span className="block">{t.hero.title[0]}</span>
            <span className="block">
              {t.hero.title[1]}{" "}
              <em className="italic font-medium text-[#d9381e]">{t.hero.title[2]}</em>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="reveal reveal-delay-2 mt-8 max-w-xl text-base sm:text-lg text-[#5c5c5c] leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <ul
            data-testid="hero-meta"
            className="reveal reveal-delay-4 mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]"
          >
            {t.hero.meta.map((m, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-[#d9381e]" />
                {m}
              </li>
            ))}
          </ul>
        </div>

        {/* Right column — Conversion card */}
        <aside
          data-testid="hero-conversion-card"
          className="reveal reveal-delay-3 lg:col-span-5 relative"
        >
          {/* Decorative offset frame */}
          <div className="absolute -inset-2 bg-[#1e2a5a]/8 rounded-2xl rotate-1 pointer-events-none" />

          <div className="relative bg-white/95 backdrop-blur-xl border border-[#1e2a5a]/15 rounded-2xl p-7 sm:p-8 shadow-[0_24px_60px_-30px_rgba(30,42,90,0.3)]">
            {/* SEO-friendly section heading (visually hidden, read by search engines + screen readers) */}
            <h2 className="sr-only">{c.seoH2}</h2>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#d9381e]/10 border border-[#d9381e]/20 text-[11px] uppercase tracking-[0.18em] font-medium text-[#d9381e]">
              <Gift className="h-3 w-3" strokeWidth={2.5} />
              {c.badge}
            </div>

            {/* Title */}
            <p className="font-display mt-5 text-2xl sm:text-3xl tracking-tight text-[#1e2a5a] leading-[1.1]">
              {c.title}
            </p>

            {/* Subtitle */}
            <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">
              {c.subtitle}
            </p>

            {/* Primary CTA */}
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-semibold text-[#f4f1ea] transition-all hover:bg-[#d9381e] hover:scale-[1.01]"
            >
              {c.primary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>

            {/* Secondary CTA — for not-ready leads */}
            <div className="mt-5 pt-5 border-t border-[#e5e3db]">
              <p className="text-xs text-[#5c5c5c] mb-2">{c.notReady}</p>
              <Link
                to="/teste-de-nivel"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-1.5 text-sm font-medium text-[#1e2a5a] link-underline"
              >
                <Sparkles className="h-3.5 w-3.5 text-[#d9381e]" />
                {c.secondary}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {/* Trust signals */}
            <ul className="mt-6 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[0.14em] text-[#5c5c5c]">
              {c.trust.map((tr, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <Check className="h-3 w-3 text-[#1e7a3e] shrink-0" strokeWidth={3} />
                  <span className="truncate">{tr}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default Hero;
